import { useCallback, useEffect, useMemo, useState } from 'react'
import { RoleDto } from '@ivy-monorepo/shared'
import { adminService } from '../../services/admin.service'
import Table from '../../components/Table'
import RoleModal from '../../components/modals/RoleModal'

type RoleTableRow = RoleDto & { permissionCount: number }

const RolesPage = () => {
  const [roles, setRoles] = useState<RoleDto[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [editingRole, setEditingRole] = useState<RoleDto | null>(null)

  const loadRoles = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const response = await adminService.getRoles()
      setRoles(response)
    } catch {
      setError('Failed to load roles.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadRoles()
  }, [loadRoles])

  const tableRows = useMemo<RoleTableRow[]>(
    () =>
      roles.map(role => ({
        ...role,
        permissionCount: role.permissions.length,
      })),
    [roles],
  )

  const handleRoleSaved = (saved: RoleDto) => {
    setRoles(prev => {
      const exists = prev.some(role => role.publicId === saved.publicId)
      if (exists) {
        return prev.map(role => (role.publicId === saved.publicId ? saved : role))
      }
      return [...prev, saved].sort((a, b) => a.name.localeCompare(b.name))
    })
  }

  const handleRowClick = (row: RoleTableRow) => {
    setEditingRole(roles.find(role => role.publicId === row.publicId) ?? null)
  }

  if (loading) {
    return <></>
  }

  return (
    <div>
      <div className="roles-container">
        <div className="roles-header">
          <h2>Roles</h2>
        </div>
        <p className="roles-page-intro">
          Manage RBAC roles and the admin modules each role can access. Staff accounts keep{' '}
          <code>kind = Admin</code>; roles control panel permissions.
        </p>

        {error && <div className="error-message">{error}</div>}

        <div className="roles-actions">
          <button
            type="button"
            onClick={() => setIsCreateModalOpen(true)}
            className="styled-button sm"
          >
            <span>Add Role</span>
          </button>
        </div>

        <div className="roles-table-container">
          <div className="table-wrapper">
            <Table
              id="roles"
              name="Roles"
              keyField="publicId"
              initialSortColumn="name"
              initialSortOrder="asc"
              columns={[
                { label: 'Name', accessor: 'name', type: 'string' },
                { label: 'Permissions', accessor: 'permissionCount', type: 'number' },
                { label: 'Updated', accessor: 'updatedAt', type: 'datetime' },
                { label: 'Created', accessor: 'createdAt', type: 'datetime' },
              ]}
              sourceData={tableRows}
              isPropertyBarVisible={false}
              filterTerms=""
              onRowClick={handleRowClick}
            />
          </div>
        </div>
      </div>

      <RoleModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        role={null}
        onSaved={handleRoleSaved}
      />

      <RoleModal
        isOpen={editingRole !== null}
        onClose={() => setEditingRole(null)}
        role={editingRole}
        onSaved={handleRoleSaved}
      />
    </div>
  )
}

export default RolesPage
